import { z } from 'zod';

import { SubscriptionStatusSchema } from './plan-schemas.js';

// Shared between backend (controller DTO + use case input) and frontend (form
// + API client). The Organization is the central multi-tenant entity; super
// admins create one per onboarded customer (JQ4-9 / US-ORG-001).

export const OrgNicheSchema = z.enum([
  'restaurant',
  'hotel',
  'event_venue',
  'hospital',
  'casino',
  'retail',
  'stadium',
  'residential',
  'corporate',
  'country_club',
]);
export type OrgNiche = z.infer<typeof OrgNicheSchema>;

// Single source of truth for niche display labels — downstream consumers
// (admin UI) must derive their option lists from these instead of
// hardcoding local 'restaurant' | 'hotel' unions. All niches beyond
// 'restaurant' and 'hotel' are categorization-only: they behave exactly
// like 'restaurant' (generic check-in, generic tariffs) — no niche-specific
// config module exists for them.
export const ORG_NICHE_LABELS: Record<OrgNiche, string> = {
  restaurant: 'Restaurant',
  hotel: 'Hotel',
  event_venue: 'Events',
  hospital: 'Hospital',
  casino: 'Casino',
  retail: 'Retail',
  stadium: 'Stadium',
  residential: 'Residential',
  corporate: 'Corporate',
  country_club: 'Country Club',
};

export const ORG_NICHE_OPTIONS: ReadonlyArray<{ value: OrgNiche; label: string }> =
  OrgNicheSchema.options.map((value) => ({ value, label: ORG_NICHE_LABELS[value] }));

export const OrgStatusSchema = z.enum(['active', 'suspended', 'deleted']);
export type OrgStatus = z.infer<typeof OrgStatusSchema>;

export const CreateOrganizationRequestSchema = z.object({
  name: z.string().trim().min(1, 'Display name is required').max(120),
  legalName: z.string().trim().min(1, 'Legal name is required').max(200),
  country: z.string().regex(/^[A-Z]{2}$/, 'Country must be an ISO 3166-1 alpha-2 code'),
  timezone: z.string().trim().min(1, 'Timezone is required').max(64),
  niche: OrgNicheSchema,
});
export type CreateOrganizationRequest = z.infer<typeof CreateOrganizationRequestSchema>;

export const OrganizationResponseSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  legalName: z.string().nullable(),
  slug: z.string(),
  country: z.string().nullable(),
  timezone: z.string().nullable(),
  niche: OrgNicheSchema.nullable(),
  status: OrgStatusSchema,
  // JQ4-36 — per-org platform commission (basis points 0..10000). Set
  // exclusively as a side effect of AssignPlanToOrganizationUseCase (derived
  // from the assigned Plan's commission toggle) — there is no standalone
  // manual-override endpoint.
  platformFeeBasisPoints: z.number().int().min(0).max(10000),
  createdAt: z.string(),
  // JQ4-47 — most-recent org_admin invitation status (additive optional field)
  firstAdminInvitationStatus: z.enum(['pending', 'expired', 'used']).nullable().optional(),
  // billing-status-visibility — additive/optional so existing consumers of
  // this response are byte-for-byte unaffected. `null` means the org has no
  // Subscription row yet (never had a plan assigned). `subscriptionStatus`
  // is the raw persisted value; `effectiveSubscriptionStatus` is what
  // ACTUALLY applies right now (a `past_due` row whose grace window has
  // elapsed, or an `incomplete` row past its onboarding deadline, reads as
  // `suspended` here even though the stored column still says otherwise —
  // see SubscriptionAccessGuard / resolveEffectiveSubscriptionStatus).
  subscriptionStatus: SubscriptionStatusSchema.nullable().optional(),
  effectiveSubscriptionStatus: SubscriptionStatusSchema.nullable().optional(),
  planName: z.string().nullable().optional(),
});
export type OrganizationResponse = z.infer<typeof OrganizationResponseSchema>;

export const ListOrganizationsResponseSchema = z.object({
  organizations: z.array(OrganizationResponseSchema),
});
export type ListOrganizationsResponse = z.infer<typeof ListOrganizationsResponseSchema>;

// AC scenario 2: after creating an organization the Super Admin must invite
// the first Org Admin in the same flow. This endpoint is a constrained alias
// of the generic invitation flow — role is implicit (`org_admin`) and the
// organizationId comes from the path param, not the JWT.
export const InviteFirstOrgAdminRequestSchema = z.object({
  email: z.string().email(),
});
export type InviteFirstOrgAdminRequest = z.infer<typeof InviteFirstOrgAdminRequestSchema>;

export const InviteFirstOrgAdminResponseSchema = z.object({
  invitationId: z.string().uuid(),
  email: z.string().email(),
  expiresAt: z.string(),
});
export type InviteFirstOrgAdminResponse = z.infer<typeof InviteFirstOrgAdminResponseSchema>;

// ── Org Branding — JQ4-13 ────────────────────────────────────────────────────
//
// Branding is scoped at the organization level (not per-location).
// The logo is stored as an S3 key in the DB; callers receive a presigned GET
// URL valid for 1 hour. The admin uploads via a presigned PUT URL so the
// backend never handles the binary.

const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

export const OrgBrandingResponseSchema = z.object({
  name: z.string(),
  primaryColor: z.string().nullable(),
  logoUrl: z.string().nullable(),
  // Additive: the org niche, so the "New location" form can default to it.
  niche: OrgNicheSchema.nullable().optional(),
});
export type OrgBrandingResponse = z.infer<typeof OrgBrandingResponseSchema>;

export const UpdateOrgBrandingRequestSchema = z
  .object({
    primaryColor: z
      .string()
      .regex(HEX_COLOR_REGEX, 'primaryColor must be a 6-digit hex color (e.g. "#1A2B3C")')
      .optional(),
    logoKey: z.string().min(1).optional(),
  })
  .refine((v) => v.primaryColor !== undefined || v.logoKey !== undefined, {
    message: 'At least one of primaryColor or logoKey must be provided',
  });
export type UpdateOrgBrandingRequest = z.infer<typeof UpdateOrgBrandingRequestSchema>;

export const LogoUploadUrlRequestSchema = z.object({
  contentType: z.enum(['image/png', 'image/jpeg'], {
    errorMap: () => ({ message: 'contentType must be image/png or image/jpeg' }),
  }),
});
export type LogoUploadUrlRequest = z.infer<typeof LogoUploadUrlRequestSchema>;

export const LogoUploadUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  key: z.string().min(1),
});
export type LogoUploadUrlResponse = z.infer<typeof LogoUploadUrlResponseSchema>;

// ── JQ4-48 — Update Organization core fields ─────────────────────────────────
//
// Partial PATCH schema: only the provided fields are written.
// At least one field must be present (at-least-one refine mirrors UpdateOrgBrandingRequestSchema).
// id, slug, createdAt are permanently immutable and intentionally absent.

export const UpdateOrganizationRequestSchema = z
  .object({
    name: z.string().trim().min(1, 'Display name is required').max(120).optional(),
    legalName: z.string().trim().min(1, 'Legal name is required').max(200).optional(),
    country: z
      .string()
      .regex(/^[A-Z]{2}$/, 'Country must be an ISO 3166-1 alpha-2 code')
      .optional(),
    timezone: z.string().trim().min(1, 'Timezone is required').max(64).optional(),
    niche: OrgNicheSchema.optional(),
    status: OrgStatusSchema.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
  });

export type UpdateOrganizationDto = z.infer<typeof UpdateOrganizationRequestSchema>;

// Repo port input type — mirrors the schema; used by UpdateOrganizationUseCase
// and PrismaOrganizationRepository.
export interface UpdateOrgCoreFieldsInput {
  name?: string;
  legalName?: string;
  country?: string;
  timezone?: string;
  niche?: OrgNiche;
  status?: OrgStatus;
}
